import {  Card, Row, Col, Button, Form, Badge } from 'react-bootstrap';
import Select from "react-select"
import {useAuth} from "../../../context/AuthContext"
import { useState, useEffect, useMemo } from 'react';
import { useUpdateProfile } from '../../../hooks/useUpdateProfile';
import { FaUser, FaUsers, FaPlus, FaTimes } from 'react-icons/fa';
import { customSelectStyles } from '../../../utils/reactSelectStyles';
import { useGroupes } from '../../../hooks/useGetGroupes';
import { useAddUserToGroups } from '../../../hooks/useAddUserToGroups';
import { useRemoveUserFromGroups } from '../../../hooks/useRemoveUserFromGroups';
import { useColors } from '../../../hooks/COLORS/useColors';
import { useRoles } from '../../../hooks/useGetRoles';

const ProfilTab = ({ userId = null, isAdmin = false, userData = null }) => {

    const { user: currentUser, loading } = useAuth();
    
    const user = isAdmin && userData ? { data: userData } : currentUser;
    const effectiveUserId = userId || currentUser?.data?.id;
    
    const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile();
    
    const { data: groupesData, isLoading: loadingGroupes } = useGroupes();
    const { mutateAsync: addToGroups} = useAddUserToGroups();
    const { mutate: removeFromGroups, isLoading: isRemoving } = useRemoveUserFromGroups();
    
    const groupes = user?.data?.groups || [];
    const allGroupes = groupesData?.data || [];
    
    const [selectedGroupe, setSelectedGroupe] = useState(null);
    const [isAddingGroupe, setIsAddingGroupe] = useState(false);

    const { data: rolesData, isLoading: loadingRoles } = useRoles();
    const roles = rolesData?.data || [];

    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        couleur: null,
        role: ''
    });

    const { data: colorsData } = useColors();
    const colorOptions = useMemo(() => 
        colorsData?.data?.map(c => ({ value: String(c.id), label: c.name, hex: c.hex })) || [], [colorsData]);

    useEffect(() => {
        if (user?.data) {
            let couleurOption = null;
            if (user.data.color?.id) {
                couleurOption = colorOptions.find(option => option.value === String(user.data.color.id));
            }
            setFormData({
                nom: user.data.surname || user.data.nom || '',
                prenom: user.data.name || user.data.prenom || '',
                email: user.data.email || '',
                telephone: user.data.phone_number || user.data.telephone || '',
                couleur: couleurOption,
                role: user.data.role?.id ? String(user.data.role.id) : ''
            });
        }
    }, [user?.data, colorOptions]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleColorChange = (selectedOption) => {
        setFormData(prev => ({ ...prev, couleur: selectedOption }));
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        if (!effectiveUserId) return;
        const updateData = {
            utilisateur: {
                nom: formData.nom,
                prenom: formData.prenom,
                telephone: formData.telephone || null,
                couleur: formData.couleur?.value ? parseInt(formData.couleur.value) : null,
                ...(isAdmin && formData.role && { role: parseInt(formData.role) })
            }
        };
        updateProfile({ userId: effectiveUserId, userData: updateData });
    };

    const handleAddGroupe = async () => {
        if (!selectedGroupe || !effectiveUserId) return;
        setIsAddingGroupe(true);
        try {
            await addToGroups({ userId: effectiveUserId, groupIds: [parseInt(selectedGroupe.value)] });
            setSelectedGroupe(null);
        } finally {
            setIsAddingGroupe(false);
        }
    };

    const handleRemoveGroupe = (groupeId) => {
        if (!effectiveUserId) return;
        removeFromGroups({ userId: effectiveUserId, groupIds: [groupeId] });
    };

    if (loading) return <div className="text-center">Chargement...</div>;

    return (
        <Row className='ml-3'>
            <Col xl={4}>
                <Card>
                    <Card.Header className="py-2 d-flex align-items-center">
                        <Card.Title>
                            <span className='text-body text-s m-0 p-0'>Photo de profil</span>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body className='d-flex flex-column justify-content-center align-items-center'>
                        <div style={{ width: '150px', height: '150px', borderRadius: '50%', backgroundColor: '#4E73DF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '60px', overflow: 'hidden', border: '3px solid #dee2e6' }}>
                            {user?.data?.avatar || user?.data?.picture ? (
                                <img src={user?.data?.avatar || user?.data?.picture} alt="Profil" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <FaUser />
                            )}
                        </div>
                        <Button variant="secondary" className='mt-3' disabled>Télécharger une nouvelle photo de profil</Button>
                    </Card.Body>
                </Card>
                
                <Card className="mt-3">
                    <Card.Header className="py-2 d-flex align-items-center">
                        <FaUsers className="me-2" />
                        <Card.Title>
                            <span className='text-muted text-s m-0 p-0'>Groupes</span>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        {groupes.length > 0 ? (
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                {groupes.map((groupe) => (
                                    <Badge
                                        key={groupe.id}
                                        style={{ fontSize: '0.9rem', padding: '0.5rem 0.75rem', backgroundColor: groupe.color?.hex || '#6c757d', color: '#fff' }}
                                        className="d-flex align-items-center gap-2"
                                    >
                                        {groupe.name}
                                        {isAdmin && (
                                            <FaTimes
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleRemoveGroupe(groupe.id)}
                                            />
                                        )}
                                    </Badge>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted small mb-3">Aucun groupe assigné</div>
                        )}
                        
                        {isAdmin && (
                            <div className="mt-3 pt-3 border-top">
                                <Form.Label className='text-muted text-sm'>Ajouter à un groupe</Form.Label>
                                <div className="d-flex gap-2">
                                    <div style={{ flex: 1 }}>
                                        <Select
                                            options={allGroupes
                                                .filter(g => !groupes.some(ug => ug.id === g.id))
                                                .map(g => ({ value: g.id.toString(), label: g.name }))}
                                            value={selectedGroupe}
                                            onChange={setSelectedGroupe}
                                            menuPortalTarget={document.body}
                                            styles={{ ...customSelectStyles, menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            placeholder="Sélectionnez un groupe"
                                            isLoading={loadingGroupes}
                                            isDisabled={isAddingGroupe || isRemoving}
                                        />
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleAddGroupe}
                                        disabled={!selectedGroupe || isAddingGroupe || isRemoving}
                                        style={{ minWidth: '80px' }}
                                    >
                                        {isAddingGroupe ? 'Chargement...' : <FaPlus />}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>
            <Col xl={7}>
                <Card>
                    <Card.Header className="py-2 d-flex align-items-center">
                        <Card.Title>
                            <span className='text-body text-s text-centered'>Détails du compte</span>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleSubmitForm}>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group controlId="idNom">
                                        <Form.Label className='text-body text-sm'>Nom</Form.Label>
                                        <Form.Control type="text" name="nom" value={formData.nom} onChange={handleInputChange} className="text-muted" />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="formLastName">
                                        <Form.Label className='text-body text-sm'>Prénom</Form.Label>
                                        <Form.Control type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} className="text-muted" />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group controlId="idEmail">
                                        <Form.Label className='text-body text-sm'>Email</Form.Label>
                                        <Form.Control type="email" name="email" className="text-muted bg-body" value={formData.email} disabled readOnly />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="formTelephone">
                                        <Form.Label className='text-body text-sm'>Téléphone</Form.Label>
                                        <Form.Control type="tel" name="telephone" value={formData.telephone} onChange={handleInputChange} placeholder='Entrez votre numéro de téléphone' className="text-muted before-input" />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className='mb-3'>
                                <Col md={6}>
                                    <Form.Group controlId="formColor">
                                        <Form.Label className='text-body text-sm'>Choisissez une couleur</Form.Label>
                                        <Select
                                            options={colorOptions}
                                            value={formData.couleur}
                                            onChange={handleColorChange}
                                            menuPortalTarget={document.body}
                                            styles={{ ...customSelectStyles, menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            placeholder="Sélectionnez une couleur"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="idColorPreview">
                                        <Form.Label className='text-body text-sm'>Prévisualisation</Form.Label>
                                        <div className="form-control d-flex align-items-center gap-2" style={{ height: '38px' }}>
                                            <div style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #ced4da', backgroundColor: formData.couleur?.hex || '#e9ecef' }} />
                                            <span className="text-muted small">{formData.couleur?.label || 'Aucune couleur sélectionnée'}</span>
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                            {isAdmin && (
                                <Row className='mb-3'>
                                    <Col md={6}>
                                        <Form.Group controlId="formRole">
                                            <Form.Label className='text-body text-sm'>Rôle</Form.Label>
                                            <Form.Select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                                disabled={loadingRoles}
                                                className="text-muted"
                                            >
                                                <option value="">Sélectionnez un rôle</option>
                                                {roles.map(r => (
                                                    <option key={r.id} value={String(r.id)}>{r.nom}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}
                            <Button variant="primary" type="submit" disabled={isUpdating || loading}>
                                {isUpdating || loading ? 'Sauvegarde...' : 'Sauvegarder les changements'}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default ProfilTab;